import React, { useState, useEffect } from 'react';
import { Link, useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { addToCart } from '../../features/cart/cartSlice';
import axios from '../../axiosConfig';
import {
  getStorefrontVariantAvailability,
  hasStorefrontVariants
} from '../../utils/storefrontVariantUtils';
import './CollectionsPage.css';

const CollectionDetailPage = () => {
  const { slug, collectionSlug } = useParams();
  const { store } = useOutletContext() || {};
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const storeSlug = slug || store?.slug;

  useEffect(() => {
    const fetchCollection = async () => {
      if (!storeSlug || !collectionSlug) return;

      setLoading(true);
      try {
        const response = await axios.get(`/collections/${storeSlug}/${collectionSlug}`);
        const data = response.data;

        if (data.success && data.data) {
          // Backend returns { success, data: { collection, store, products } }
          setCollection({
            ...data.data.collection,
            products: data.data.products?.map(p => ({ product: p })) || []
          });
        } else {
          setError(data.error || 'Collection not found');
        }
      } catch (err) {
        console.error('Error fetching collection:', err);
        setError('Failed to load collection');
      } finally {
        setLoading(false);
      }
    };

    fetchCollection();
  }, [storeSlug, collectionSlug]);

  const getProductUrl = (product) => {
    if (slug) {
      return `/s/${slug}/product/${product.slug}`;
    }
    return `/product/${product.slug}`;
  };

  const handleProductAction = (product, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (hasStorefrontVariants(product)) {
      navigate(getProductUrl(product));
      return;
    }

    dispatch(addToCart({ product, quantity: 1 }));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: store?.currency || 'NGN',
      minimumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return (
      <div className="storefront-collection-detail">
        <div className="collections-loading">
          <div className="loading-spinner"></div>
          <p>Loading collection...</p>
        </div>
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div className="storefront-collection-detail">
        <div className="collections-error">
          <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3>Collection not found</h3>
          <p>{error || 'This collection may have been removed or is no longer available.'}</p>
          <Link to={slug ? `/s/${slug}/collections` : '/collections'} className="back-link">
            View all collections
          </Link>
        </div>
      </div>
    );
  }

  const products = collection.products?.map(cp => cp.product).filter(Boolean) || [];

  return (
    <div className="storefront-collection-detail">
      {/* Collection Header */}
      <div className="collection-detail-header">
        {(collection.coverImage || collection.image) && (
          <div className="collection-cover-image">
            <img src={collection.coverImage || collection.image} alt={collection.name} />
            <div className="collection-cover-overlay" />
          </div>
        )}
        <div className="collection-header-content">
          <nav className="collection-breadcrumb">
            <Link to={slug ? `/s/${slug}/collections` : '/collections'}>Collections</Link>
            <span>/</span>
            <span>{collection.name}</span>
          </nav>
          <h1>{collection.name}</h1>
          {collection.description && (
            <p className="collection-description">{collection.description}</p>
          )}
          <span className="collection-count">{products.length} products</span>
        </div>
      </div>

      {/* Products Grid */}
      {products.length === 0 ? (
        <div className="collection-empty">
          <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <p>No products in this collection yet.</p>
        </div>
      ) : (
        <div className="collection-products-grid">
          {products.map((product) => {
            const availability = getStorefrontVariantAvailability(product);
            const isOutOfStock = !availability.hasStock;

            return (
              <Link
                key={product.id}
                to={getProductUrl(product)}
                className="collection-product-card"
              >
                <div className="product-card-image">
                  {(product.primaryImage || product.gallery?.[0]) ? (
                    <img src={product.primaryImage || product.gallery?.[0]} alt={product.name} />
                  ) : (
                    <div className="product-card-placeholder">
                      <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <path d="m3 15 6-6 4 4 8-8" />
                      </svg>
                    </div>
                  )}
                  {product.compareAtPrice && product.compareAtPrice > product.price && (
                    <span className="product-sale-badge">Sale</span>
                  )}
                </div>
                <div className="product-card-content">
                  <h3 className="product-card-name">{product.name}</h3>
                  <div className="product-card-pricing">
                    <span className="product-price">{formatPrice(product.price)}</span>
                    {product.compareAtPrice && product.compareAtPrice > product.price && (
                      <span className="product-compare-price">{formatPrice(product.compareAtPrice)}</span>
                    )}
                  </div>
                  {!isOutOfStock ? (
                    <button
                      className="product-add-to-cart"
                      onClick={(e) => handleProductAction(product, e)}
                    >
                      {hasStorefrontVariants(product) ? 'Select Options' : 'Add to Cart'}
                    </button>
                  ) : (
                    <span className="product-out-of-stock">Out of Stock</span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CollectionDetailPage;
